import styled from "styled-components";
import { Flex, Text } from "rebass/styled-components";
import { HiExclamationCircle } from "react-icons/hi";
import { TiCloudStorage } from "react-icons/ti";
import { useSetRecoilState } from "recoil";

import { ModalType } from "MVVM/@types";
import { AllstarModalState } from "MVVM/State/modals";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";

const TOOLTIP_TEXT =
  "All new clips starts as previews and expire. Save a clip to keep it permanently — you can always replace it with another clip later.";

interface ClipExpirationBannerProps {
  clipId: string;
  clipTitle: string;
  clipThumbnail: string;
  isOwner: boolean;
  isRemixable?: boolean;
}

export const ClipExpirationBanner = ({
  clipId,
  clipTitle,
  clipThumbnail,
  isOwner,
  isRemixable,
}: ClipExpirationBannerProps) => {
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const { allstarUser } = useUser();
  const isLoggedIn = !!allstarUser?.loggedIn;

  const handleSave = () => {
    setAllstarModalState({
      data: {
        clipId,
        clipTitle,
        clipThumbnail,
      },
      functions: {},
      isOpen: ModalType.SaveClipFlow,
    });

    analytics.track("Save Clip - Open Flow", {
      clipId,
      source: "clip_experience_expiration_banner",
    });
  };

  const getExpiryMessage = () => {
    if (!isLoggedIn) {
      return "This clip will expire soon — log in to save it";
    }
    if (!isOwner && isRemixable) {
      return "This clip will expire soon — remix it to keep it";
    }
    return "This clip will expire soon";
  };

  return (
    <BannerContainer>
      <AnimatedTooltip content={TOOLTIP_TEXT} position="bottom" maxWidth={240}>
        <ExpiryRow>
          <WarningIcon>
            <HiExclamationCircle size={16} />
          </WarningIcon>
          <ExpiryText>{getExpiryMessage()}</ExpiryText>
        </ExpiryRow>
      </AnimatedTooltip>
      {isLoggedIn && isOwner && (
        <ActionButton onClick={handleSave}>
          <TiCloudStorage size={16} />
          <ButtonLabel>Save</ButtonLabel>
        </ActionButton>
      )}
    </BannerContainer>
  );
};

const BannerContainer = styled(Flex)`
  align-items: center;
  background: rgba(255, 50, 50, 0.1);
  border-bottom: 2px solid ${({ theme }) => theme.colors.error};
  justify-content: space-between;
  padding: 12px 48px 12px 16px;
  width: 100%;
`;

const ExpiryRow = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const WarningIcon = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.error};
  flex-shrink: 0;
`;

const ExpiryText = styled(Text)`
  color: ${({ theme }) => theme.colors.error};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const ActionButton = styled.button`
  align-items: center;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  height: 32px;
  justify-content: center;
  padding: 0 14px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const ButtonLabel = styled(Text)`
  font-family: Barlow;
  font-size: 13px;
  font-weight: 600;
`;
